import { useId, type ReactElement, type ReactNode } from 'react';
import { Icon } from '../Icon/Icon';
import styles from './Accordion.module.scss';

export interface AccordionProps {
  /** Разделы — AccordionSection. */
  children: ReactNode;
  /** Подпись группы разделов для чтения с экрана. */
  label?: string;
}

/** Набор сворачиваемых разделов на одной поверхности. */
export function Accordion({ children, label }: AccordionProps): ReactElement {
  return (
    <div className={styles.accordion} role="group" aria-label={label}>
      {children}
    </div>
  );
}

export interface AccordionSectionProps {
  title: ReactNode;
  /** Справа от заголовка: число элементов, отметки. */
  meta?: ReactNode;
  open: boolean;
  onToggle(open: boolean): void;
  /** Содержимое рисуется, только пока раздел раскрыт: свёрнутые разделы ничего не стоят. */
  children: ReactNode;
}

/** Раздел аккордеона: кнопка-заголовок с aria-expanded и область содержимого (WAI-ARIA accordion). */
export function AccordionSection({ title, meta, open, onToggle, children }: AccordionSectionProps): ReactElement {
  const id = useId();
  const headerId = `${id}-header`;
  const panelId = `${id}-panel`;
  return (
    <section className={styles.section}>
      <h3 className={styles.heading}>
        <button type="button" id={headerId} className={styles.trigger} aria-expanded={open} aria-controls={panelId} onClick={() => onToggle(!open)}>
          <span className={open ? `${styles.chevron} ${styles.chevronOpen}` : styles.chevron} aria-hidden>
            <Icon name="chevron" size={18} />
          </span>
          <span className={styles.title}>{title}</span>
          {meta !== undefined ? <span className={styles.meta}>{meta}</span> : null}
        </button>
      </h3>
      {open ? (
        <div id={panelId} role="region" aria-labelledby={headerId} className={styles.panel}>
          {children}
        </div>
      ) : null}
    </section>
  );
}
